import React, { useEffect } from 'react'

import { DisplayOptions, initialize, getInAppMessages } from '@iterable/web-sdk'
import { useInView } from 'react-cool-inview'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, oneOf, shape, string, number } from 'prop-types'

import SubscriptionEntryPoints from 'Pods/Shared/Constants/subscriptionEntryPoints'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import i18n from 'Pods/Shared/I18nHome'
import { setSubscriptionEntry } from 'Pods/Shared/Utils/subscriptionEntry'

import roundHumanCount from 'Utils/roundHumanCount'

import useSubscriptionPromoTracking from './useSubscriptionPromoTracking'

function SubscriptionPromoSection({
  images,
  locale = 'en',
  ctaUrl = '/subscription',
  extractingCoreStyles = false,
  subscriptionDownloadsPerMonth = 25,
  subscriptionFlowEnabled = null,
  isActiveSubscriber = null,
  subscriptionCatalogValue = null,
  subscriptionPremiumCatalogValue = null,
  premiumExposed = null,
  variant = 'homepage',
  trackPromo = true,
}) {
  const pageConfig = usePageConfig()
  const { trackImpression, trackCtaClick } = useSubscriptionPromoTracking()

  const iterableInBrowserMessageApiKey =
    pageConfig?.iterableInBrowserMessageApiKey
  const iterableJwt = pageConfig?.iterableJwt
  const currentUserEmail = pageConfig?.currentUserEmail

  useEffect(() => {
    let isMounted = true

    const initializeIterable = async () => {
      if (
        typeof window === 'undefined' ||
        !iterableInBrowserMessageApiKey ||
        !iterableJwt ||
        !currentUserEmail
      ) {
        return
      }

      const { setEmail } = initialize(
        iterableInBrowserMessageApiKey,
        async () => iterableJwt
      )

      await setEmail(currentUserEmail)

      if (isMounted) {
        const inAppMessages = getInAppMessages(
          {
            count: 20,
            packageName: 'dev-site',
          },
          {
            display: DisplayOptions.Immediate,
          }
        )

        await inAppMessages.request()
      }
    }

    initializeIterable()

    return () => {
      isMounted = false
    }
  }, [currentUserEmail, iterableInBrowserMessageApiKey, iterableJwt])

  const translate = (key, options = {}) =>
    i18n.t(key, {
      ns: 'new-home',
      lng: locale,
      count: subscriptionDownloadsPerMonth,
      ...options,
    })

  const resolvedSubscriptionFlowEnabled =
    typeof subscriptionFlowEnabled === 'boolean'
      ? subscriptionFlowEnabled
      : pageConfig?.subscriptionFlowEnabled
  const shouldHideBanner =
    variant !== 'profile-settings' &&
    (resolvedSubscriptionFlowEnabled !== true || isActiveSubscriber === true)

  const { observe } = useInView({
    threshold: 0.25,
    onEnter: ({ unobserve }) => {
      unobserve()

      if (trackPromo) {
        trackImpression()
      }
    },
  })

  const entryPoint = variant === 'homepage'
    ? SubscriptionEntryPoints.LANDING_HOMEPAGE_PROMO
    : SubscriptionEntryPoints.LANDING_SETTINGS_PROMO

  const handleCtaClick = () => {
    if (trackPromo) {
      trackCtaClick()
      setSubscriptionEntry(entryPoint)
    }
  }

  if (!extractingCoreStyles && shouldHideBanner) {
    return null
  }

  const isHomepageVariant = variant === 'homepage'
  const sectionClassName = classnames(
    'subscription-banner',
    isHomepageVariant && 'new-home-section'
  )
  const containerClassName = classnames(
    'subscription-banner__container',
    variant === 'profile-settings' &&
      'subscription-banner__container--profile-settings'
  )

  // Premium-exposed viewers see the total nested catalog (~1.2M); everyone else keeps the
  // Essentials count. On fast pages (homepage, item page) premiumExposed resolves client-side
  // (pageConfig) so SSR renders the Essentials default and swaps in place after hydration. On
  // slow pages (profile settings) pageConfig is never populated -- /load-services.js is not
  // fetched there -- so the server passes premium_exposed? in as a prop.
  const resolvedPremiumExposed =
    typeof premiumExposed === 'boolean' ? premiumExposed : !!pageConfig?.premiumExposed
  const rawCount = resolvedPremiumExposed && subscriptionPremiumCatalogValue
    ? subscriptionPremiumCatalogValue
    : subscriptionCatalogValue
  const formattedCount = rawCount ? roundHumanCount(rawCount) : null

  const tags = [
    formattedCount
      ? translate('subscriptionPromo.tags.includes', {
          modelCount: formattedCount,
        })
      : null,
    translate('subscriptionPromo.tags.royaltyFree'),
    translate('subscriptionPromo.tags.newModels'),
  ].filter(Boolean)

  return (
    <section
      className={sectionClassName}
      aria-label={translate('subscriptionPromo.ariaLabel')}
      ref={observe}
    >
      <div className={containerClassName}>
        <picture className="subscription-banner__wave">
          <source media="(min-width: 1920px)" srcSet={images.waveLarge} />
          {/* Mobile LCP element on the homepage: must not be lazy-loaded there */}
          <img
            className="subscription-banner__wave-image"
            src={images.wave}
            alt=""
            loading={isHomepageVariant ? undefined : 'lazy'}
            fetchpriority={isHomepageVariant ? 'high' : undefined}
            decoding="async"
            width={1180}
            height={390}
          />
        </picture>

        <div className="subscription-banner__content">
          <h2 className="subscription-banner__title cgt-legacy-heading-h2">
            {translate('subscriptionPromo.title')}
          </h2>

          <ul
            className="subscription-banner__tags"
            aria-label={translate('subscriptionPromo.tagsAriaLabel')}
          >
            {tags.map((tag) => (
              <li key={tag} className="subscription-banner__tag">
                {tag}
              </li>
            ))}
          </ul>

          <a
            href={ctaUrl}
            className="cgt-button cgt-button--primary subscription-banner__cta"
            data-subscription-entry={entryPoint}
            onClick={handleCtaClick}
          >
            <span>{translate('subscriptionPromo.ctaButton')}</span>
          </a>

          <p className="subscription-banner__subtext">
            {translate('subscriptionPromo.subtext')}
          </p>
        </div>

        <div className="subscription-banner__visual" aria-hidden="true">
          <img
            className="subscription-banner__butterfly"
            src={images.butterfly}
            alt=""
            loading="lazy"
            decoding="async"
          />

          <img
            className="subscription-banner__bird"
            src={images.bird}
            alt=""
            loading="lazy"
            decoding="async"
          />

          <img
            className="subscription-banner__flower"
            src={images.flower}
            alt=""
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="subscription-banner__value-card" aria-hidden="true">
          <p className="subscription-banner__value-top">
            {translate('subscriptionPromo.valueCard.plans')}
          </p>
          {/* No separators here: CJK locales set them to empty, Latin ones carry them */}
          <p className="subscription-banner__value-bottom">
            {translate('subscriptionPromo.valueCard.from')}
            <strong className="subscription-banner__value-price">$9.99</strong>
            {translate('subscriptionPromo.valueCard.perMonth')}
          </p>
        </div>

        <div className="subscription-banner__everyday-pill" aria-hidden="true">
          {translate('subscriptionPromo.everydayNeeds')}
        </div>
      </div>
    </section>
  )
}

SubscriptionPromoSection.propTypes = {
  images: shape({
    wave: string.isRequired,
    waveLarge: string.isRequired,
    butterfly: string.isRequired,
    bird: string.isRequired,
    flower: string.isRequired,
  }).isRequired,
  locale: string,
  ctaUrl: string,
  extractingCoreStyles: bool,
  subscriptionDownloadsPerMonth: number,
  subscriptionFlowEnabled: bool,
  isActiveSubscriber: bool,
  subscriptionCatalogValue: string,
  subscriptionPremiumCatalogValue: string,
  premiumExposed: bool,
  variant: oneOf(['homepage', 'profile-settings']),
  trackPromo: bool,
}

export default SubscriptionPromoSection
